import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ChevronLeft, ChevronRight, LayoutGrid, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authFetch = async (path) => {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
  return res.json();
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shift = (s, days) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + days); return iso(d); };
const fmt = (s, o) => new Date(`${s}T00:00:00`).toLocaleDateString('en-GB', o);
const CELL = {
  done: 'bg-emerald-100 text-emerald-900 hover:bg-emerald-200',
  missing: 'bg-red-100 text-red-900 hover:bg-red-200 font-semibold',
  future: 'bg-gray-50 text-gray-300',
  holiday: 'bg-gray-50 text-gray-300',
};

export default function AttendanceOverviewPage() {
  const navigate = useNavigate();
  const [week, setWeek] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/student-attendance/overview?week=${week}`).then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [week]);

  const rows = (data?.rows || []).filter((r) => !onlyMissing || r.missing > 0);
  const open = (r, d) => { if (d.status === 'done' || d.status === 'missing') navigate(`/dashboard/roll-call?class=${r.class_id}&date=${d.date}`); };

  return (
    <div className="space-y-6" data-testid="attendance-overview-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><LayoutGrid className="h-6 w-6 text-primary" /> Attendance Overview</h1>
          <p className="text-sm text-muted-foreground">Every class for the week — red cells are roll calls that have not been taken. Tap a cell to open that day.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setWeek(shift(week, -7))} data-testid="overview-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium min-w-[190px] text-center" data-testid="overview-week-label">{data ? `${fmt(data.week_start, { day: 'numeric', month: 'short' })} – ${fmt(data.week_end, { day: 'numeric', month: 'short', year: 'numeric' })}` : '…'}</span>
          <Button variant="ghost" size="icon" onClick={() => setWeek(shift(week, 7))} data-testid="overview-next-week"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeek(iso(new Date()))} data-testid="overview-this-week">This week</Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3" data-testid="overview-summary">
          <div className="rounded-lg border bg-card p-4"><p className="text-xs text-muted-foreground">Classes</p><p className="text-2xl font-bold">{data.summary.classes}</p></div>
          <button onClick={() => setOnlyMissing(!onlyMissing)} className={`text-left rounded-lg border bg-card p-4 hover:bg-muted/50 ${onlyMissing ? 'ring-2 ring-red-500' : ''}`} data-testid="overview-filter-missing"><p className="text-xs text-muted-foreground">Classes with missing roll calls</p><p className="text-2xl font-bold text-red-700">{data.summary.with_missing}</p></button>
          <div className="rounded-lg border bg-card p-4"><p className="text-xs text-muted-foreground">Missing roll calls this week</p><p className="text-2xl font-bold text-red-700">{data.summary.missing_total}</p></div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2">{data?.term || 'Holiday'} {loading && <Loader2 className="h-4 w-4 animate-spin" />}</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="overview-grid">
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th className="text-left px-4 py-2 font-medium">Class</th>
                <th className="text-left px-2 py-2 font-medium hidden md:table-cell">Teacher</th>
                {(data?.days || []).map((d) => <th key={d} className={`px-1 py-2 font-medium text-center ${d === data.today ? 'text-primary' : ''}`}>{fmt(d, { weekday: 'short' })}<br /><span className="font-normal">{fmt(d, { day: 'numeric' })}</span></th>)}
                <th className="px-2 py-2 font-medium text-center">Missing</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b last:border-0" data-testid={`overview-row-${r.key}`}>
                  <td className="px-4 py-1.5 font-medium whitespace-nowrap">{r.name}</td>
                  <td className="px-2 py-1.5 text-muted-foreground hidden md:table-cell whitespace-nowrap">{r.teacher_name || <span className="italic">no teacher</span>}</td>
                  {r.days.map((d) => (
                    <td key={d.date} className="px-1 py-1">
                      <button onClick={() => open(r, d)} disabled={d.status === 'future' || d.status === 'holiday'} className={`w-full h-9 rounded text-xs ${CELL[d.status]}`} data-testid={`overview-cell-${r.key}-${d.date}`} data-status={d.status}>
                        {d.status === 'done' ? (d.periods.some((p) => p.period !== 'daily') ? d.periods.map((p) => p.period.replace('period', 'P')).join(' ') : '✓') : d.status === 'missing' ? '!' : ''}
                      </button>
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-center">{r.missing > 0 ? <Badge variant="destructive" data-testid={`overview-missing-${r.key}`}>{r.missing}</Badge> : <Badge className="bg-emerald-600">0</Badge>}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground"><AlertTriangle className="inline h-4 w-4 mr-1" /> No classes to show.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
